import React, { useState, useEffect, useRef } from 'react';
import './JobSearchDropdown.css';

const JobSearchDropdown = ({ jobs, selectedJobId, onSelectJob, placeholder = "Search jobs..." }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredJobs, setFilteredJobs] = useState(jobs);
  const dropdownRef = useRef(null);

  // Filter jobs based on search term (prioritize job title)
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredJobs(jobs);
    } else {
      const filtered = jobs.filter(job => {
        const title = job.title || job.jobTitle || '';
        const company = job.company || '';
        const location = job.location || '';
        const id = job.id?.toString() || '';

        return (
          title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          id.includes(searchTerm) ||
          company.toLowerCase().includes(searchTerm.toLowerCase()) ||
          location.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
      setFilteredJobs(filtered);
    }
  }, [searchTerm, jobs]);

  // Get selected job details
  const selectedJob = jobs.find(job => job.id === selectedJobId);

  // Auto-select job when selectedJobId prop changes (e.g., from Upload Resumes button)
  useEffect(() => {
    if (selectedJobId && selectedJob) {
      // Job is already selected, no need to do anything
      setIsOpen(false);
    }
  }, [selectedJobId, selectedJob]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectJob = (job) => {
    onSelectJob(job);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div className="job-search-dropdown" ref={dropdownRef}>
      <div className="job-search-input-wrapper">
        <input
          type="text"
          className="job-search-input"
          placeholder={selectedJob ? `${selectedJob.title || selectedJob.jobTitle || 'Job'} (ID: ${selectedJob.id})` : placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
        />
        <svg
          className="job-search-icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="job-search-results">
          {filteredJobs.length > 0 ? (
            filteredJobs.map((job) => (
              <div
                key={job.id}
                className={`job-search-item ${selectedJobId === job.id ? 'selected' : ''}`}
                onClick={() => handleSelectJob(job)}
              >
                <div className="job-search-item-title">
                  {job.title || job.jobTitle || 'Untitled Job'}
                </div>
                <div className="job-search-item-meta">
                  ID: {job.id} • {job.company} • {job.location}
                </div>
              </div>
            ))
          ) : (
            <div className="job-search-no-results">
              No jobs found matching "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JobSearchDropdown;